// Ubicación en el repo: src/components/QuoteFormModal.jsx
// Modal Crear/Editar cotización de audífonos (migración 0051, 26-ago-2026)
// — pedido de Nicolás: uno o varios modelos con precio, para un paciente
// con ficha o un prospecto que todavía no la tiene. `catalog` llega ya
// cargado y filtrado (categorySpecialRole: 'audifono', branch-scoped)
// desde HearingAids.jsx — mismo patrón que HearingAidFormModal.jsx, no se
// vuelve a pedir acá.
import { useState } from 'react';
import { createQuote, updateQuote } from '../lib/hearingaidQuotes';
import { searchPatients } from '../lib/patients';
import { formatPatientName, todayISO, addDaysISO } from '../lib/format';

function initialForm(quote) {
  if (quote) {
    return {
      isProspect: !quote.patient_id,
      patientId: quote.patient_id,
      patientLabel: quote.patients ? formatPatientName(quote.patients) : '',
      prospectName: quote.prospect_name || '',
      prospectPhone: quote.prospect_phone || '',
      prospectEmail: quote.prospect_email || '',
      validUntil: quote.valid_until || '',
      paymentTerms: quote.payment_terms || '',
      notes: quote.notes || '',
    };
  }
  return {
    isProspect: false,
    patientId: null,
    patientLabel: '',
    prospectName: '',
    prospectPhone: '',
    prospectEmail: '',
    validUntil: addDaysISO(todayISO(), 15),
    paymentTerms: '',
    notes: '',
  };
}

function initialItems(quote, catalog) {
  if (quote?.items?.length) {
    return quote.items.map((it) => ({ catalogId: it.catalog_id, quotedPrice: String(it.quoted_price ?? 0) }));
  }
  if (catalog.length) {
    return [{ catalogId: catalog[0].id, quotedPrice: String(catalog[0].price ?? 0) }];
  }
  return [];
}

export default function QuoteFormModal({ quote, branchId, catalog, actorId, onClose, onSaved }) {
  const isEdit = !!quote;
  const [form, setForm] = useState(() => initialForm(quote));
  const [items, setItems] = useState(() => initialItems(quote, catalog));
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function selectPatient(p) {
    setForm((f) => ({ ...f, patientId: p.id, patientLabel: formatPatientName(p) }));
    setPatientQuery('');
    setPatientResults([]);
  }

  async function handlePatientSearch(value) {
    setPatientQuery(value);
    if (!value.trim()) {
      setPatientResults([]);
      return;
    }
    try {
      setPatientResults(await searchPatients(value));
    } catch {
      setPatientResults([]);
    }
  }

  function addItemRow() {
    const used = new Set(items.map((it) => it.catalogId));
    const next = catalog.find((c) => !used.has(c.id)) || catalog[0];
    if (!next) return;
    setItems((its) => [...its, { catalogId: next.id, quotedPrice: String(next.price ?? 0) }]);
  }

  function updateItemModel(index, catalogId) {
    const c = catalog.find((x) => x.id === catalogId);
    setItems((its) => its.map((it, i) => (i === index ? { catalogId, quotedPrice: String(c?.price ?? 0) } : it)));
  }

  function updateItemPrice(index, price) {
    setItems((its) => its.map((it, i) => (i === index ? { ...it, quotedPrice: price } : it)));
  }

  function removeItemRow(index) {
    setItems((its) => its.filter((_, i) => i !== index));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!form.isProspect && !form.patientId) {
      setError('Selecciona un paciente, o marca "Prospecto sin ficha".');
      return;
    }
    if (form.isProspect && !form.prospectName.trim()) {
      setError('Indica el nombre del prospecto.');
      return;
    }
    if (items.length === 0) {
      setError('Agrega al menos un modelo a la cotización.');
      return;
    }
    setSaving(true);
    try {
      const values = {
        branchId,
        patientId: form.isProspect ? null : form.patientId,
        prospectName: form.prospectName,
        prospectPhone: form.prospectPhone,
        prospectEmail: form.prospectEmail,
        validUntil: form.validUntil,
        paymentTerms: form.paymentTerms,
        notes: form.notes,
      };
      if (isEdit) {
        // `actorId` ya no se manda acá — updateQuote() ahora pasa por una
        // función de servidor que fija `updated_by` sola, ver DECISIONS.md
        // #121.
        await updateQuote(quote.id, values, items);
      } else {
        await createQuote(values, items, actorId);
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la cotización.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-card--wide">
        <h2>{isEdit ? 'Editar cotización' : 'Nueva cotización'}</h2>

        {catalog.length === 0 ? (
          <>
            <p className="field-hint">
              Antes de armar una cotización, agrega al menos una marca y modelo en <strong>Catálogo</strong>.
            </p>
            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="patient-form">
            <div className="checkbox-group" style={{ marginBottom: 4 }}>
              <button
                type="button"
                className={!form.isProspect ? 'button-primary' : 'button-secondary'}
                onClick={() => set('isProspect', false)}
              >
                Paciente con ficha
              </button>
              <button
                type="button"
                className={form.isProspect ? 'button-primary' : 'button-secondary'}
                onClick={() => set('isProspect', true)}
              >
                Prospecto sin ficha
              </button>
            </div>

            {!form.isProspect ? (
              <div className="field">
                <span>Paciente *</span>
                {form.patientId ? (
                  <div className="patient-picker-selected">
                    {form.patientLabel}
                    <button type="button" className="link-button" onClick={() => set('patientId', null)}>
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      value={patientQuery}
                      onChange={(e) => handlePatientSearch(e.target.value)}
                      placeholder="Buscar por nombre o RUT…"
                    />
                    {patientResults.length > 0 && (
                      <ul className="patient-picker-results">
                        {patientResults.map((p) => (
                          <li key={p.id} onClick={() => selectPatient(p)}>
                            {formatPatientName(p)} {p.rut ? `· ${p.rut}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="form-row">
                <label className="field">
                  <span>Nombre *</span>
                  <input value={form.prospectName} onChange={(e) => set('prospectName', e.target.value)} />
                </label>
                <label className="field">
                  <span>Teléfono</span>
                  <input value={form.prospectPhone} onChange={(e) => set('prospectPhone', e.target.value)} placeholder="Opcional" />
                </label>
                <label className="field">
                  <span>Correo</span>
                  <input value={form.prospectEmail} onChange={(e) => set('prospectEmail', e.target.value)} placeholder="Opcional" />
                </label>
              </div>
            )}

            <div className="field">
              <span>Modelos cotizados *</span>
              {items.map((it, i) => (
                <div key={i} className="form-row" style={{ alignItems: 'flex-end' }}>
                  <label className="field">
                    <span>Modelo</span>
                    <select value={it.catalogId} onChange={(e) => updateItemModel(i, e.target.value)}>
                      {catalog.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.brand_ref?.name || c.brand} {c.model}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Precio (CLP)</span>
                    <input type="number" value={it.quotedPrice} onChange={(e) => updateItemPrice(i, e.target.value)} />
                  </label>
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    disabled={items.length === 1}
                    onClick={() => removeItemRow(i)}
                    style={{ marginBottom: 10 }}
                  >
                    Quitar
                  </button>
                </div>
              ))}
              <button type="button" className="link-button" onClick={addItemRow}>
                + Agregar modelo para comparar
              </button>
            </div>

            <div className="form-row">
              <label className="field">
                <span>Válida hasta</span>
                <input type="date" value={form.validUntil} onChange={(e) => set('validUntil', e.target.value)} />
              </label>
            </div>
            <label className="field">
              <span>Condiciones de pago / financiamiento</span>
              <textarea
                value={form.paymentTerms}
                onChange={(e) => set('paymentTerms', e.target.value)}
                rows={2}
                placeholder="Ej: Contado, 3 cuotas sin interés con tarjeta X. Opcional."
              />
            </label>
            <label className="field">
              <span>Notas</span>
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={2} placeholder="Opcional" />
            </label>

            {error && <p className="auth-error">{error}</p>}

            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className="button-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cotización'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
